import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { marked } from 'marked';

Answer.propTypes = {
  markdown: PropTypes.string.isRequired
};

// Answers are authored as markdown in translation.json and rendered the same
// way as the rest of the app (marked + markdown-content), so bold and lists
// survive translation. See YourInvolvement.js for the established pattern.
function Answer(props) {
  const { markdown } = props;

  return (
    <div
      className="markdown-content text-base leading-normal text-cs-gray-500"
      dangerouslySetInnerHTML={{ __html: marked.parse(markdown) }}
    />
  );
}

FaqAccordion.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    question: PropTypes.string.isRequired,
    answer: PropTypes.string.isRequired
  }))
};

export default function FaqAccordion(props) {
  const { t } = useTranslation();
  const { items } = props;

  const faqItems = items ?? t('projects.share.faq.items', { returnObjects: true });

  return (
    <article className="w-full max-w-screen-xl mx-auto bg-white rounded-cs-10 shadow-lg overflow-hidden">
      <div className="flex flex-col">
        {faqItems.map((item, i) => (
          <div key={i}>
            {i > 0 && <div className="h-px w-full bg-cs-gray-300" />}
            <Disclosure>
              {({ open }) => (
                <>
                  <Disclosure.Button className="w-full flex items-center justify-between gap-4 px-5 py-6 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-inset">
                    <span className="text-base leading-normal font-semibold text-cs-gray-900">
                      {item.question}
                    </span>
                    <ChevronDownIcon
                      aria-hidden="true"
                      className={classNames(
                        'w-5 h-5 text-cs-navy-800 shrink-0 transition-transform duration-200',
                        open && 'rotate-180'
                      )}
                    />
                  </Disclosure.Button>
                  {/* Kept mounted (`static`) so the open/close height can
                      animate; grid-rows 0fr->1fr is the project's collapse
                      transition (see YourInvolvement.js). */}
                  <Disclosure.Panel
                    static
                    className={classNames(
                      'grid transition-all duration-300 ease-in-out',
                      open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                    )}
                  >
                    <div className="overflow-hidden">
                      <div className="px-5 pb-7">
                        <Answer markdown={item.answer} />
                      </div>
                    </div>
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          </div>
        ))}
      </div>
    </article>
  );
}
